import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { format, addDays, setHours, startOfHour } from 'date-fns';
import { X, AlarmClock } from 'lucide-react';
import { reconnectionLeadAPI } from '../../services/api';

// Call notes for one inactive CableTV customer — shared by Reconnection Calls and Discontinued
// CableTV (same notes on both). A follow-up reminder also lands in the Notes section (backend
// creates a personal Note), which WhatsApps it at that time — same as Renewals' call notes.
const DT_INPUT = "yyyy-MM-dd'T'HH:mm";

export function ReminderBadge({ reminder }) {
  if (!reminder?.remindAt) return null;
  const sent = reminder.status === 'sent';
  return (
    <span title={sent ? 'Reminder sent' : 'Reminder pending'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: sent ? 'var(--text-muted)' : 'var(--primary)', fontWeight: 600 }}>
      <AlarmClock size={10} /> {format(new Date(reminder.remindAt), 'dd/MM h:mm a')}{sent ? ' (sent)' : ''}
    </span>
  );
}

// `lead` = { name, accountNumber, leadKey?, society, houseNumber, mobile, notes }.
// onSaved(lead document from the API: { notes, status, lastCalledAt }).
export default function LeadNotesModal({ lead, onClose, onSaved }) {
  const [text, setText] = useState('');
  const [remind, setRemind] = useState(false);
  const [remindAt, setRemindAt] = useState('');
  const [saving, setSaving] = useState(false);

  const quick = (days) => setRemindAt(format(startOfHour(setHours(addDays(new Date(), days), 10)), DT_INPUT));

  const save = async () => {
    if (!text.trim()) return;
    if (remind && !remindAt) { toast.error('Pick a reminder time'); return; }
    setSaving(true);
    try {
      const { data } = await reconnectionLeadAPI.addNote({
        leadKey: lead.leadKey || lead.accountNumber,
        text,
        remindAt: remind ? new Date(remindAt).toISOString() : undefined,
        label: `${lead.name || lead.accountNumber} (${[lead.society, lead.houseNumber].filter(Boolean).join(' ')}, ${lead.mobile || 'no mobile'})`,
      });
      toast.success(remind ? 'Note saved, reminder added to Notes' : 'Note saved');
      onSaved(data.data);
      setText(''); setRemind(false); setRemindAt('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save note');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3>Notes — {lead.name || lead.accountNumber}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {lead.notes.length === 0 ? (
            <p className="text-muted text-small">No notes yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto', marginBottom: 12 }}>
              {[...lead.notes].reverse().map(n => (
                <div key={n._id} style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{n.text}</div>
                  <div className="text-muted" style={{ fontSize: '.72rem', marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span>{n.createdBy?.name || '—'} · {format(new Date(n.createdAt), 'dd MMM yyyy, h:mm a')}</span>
                    <ReminderBadge reminder={n.reminderNote} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <textarea className="form-control" rows={3} value={text} onChange={e => setText(e.target.value)}
            placeholder="What did the customer say? e.g. Will restart next month, wants cheaper pack…" autoFocus />
          <div style={{ marginTop: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', fontWeight: 600, cursor: 'pointer' }}>
              <input type="checkbox" checked={remind} onChange={e => { setRemind(e.target.checked); if (e.target.checked && !remindAt) quick(1); }} />
              <AlarmClock size={14} /> Set follow-up reminder (goes to Notes + WhatsApp)
            </label>
            {remind && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                <button type="button" className="btn btn-sm" onClick={() => quick(1)}>Tomorrow 10 AM</button>
                <button type="button" className="btn btn-sm" onClick={() => quick(2)}>In 2 days</button>
                <input type="datetime-local" className="form-control" style={{ width: 210, padding: '4px 8px' }}
                  value={remindAt} min={format(new Date(), DT_INPUT)} onChange={e => setRemindAt(e.target.value)} />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !text.trim()}>
              {saving ? 'Saving…' : 'Add Note'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
